import { ReactElement } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import {
  ConfirmIcon,
  RefreshIcon,
  ReviewContentIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'
import { CourseV2QuizResult } from 'src/v3/classroom-v2/types'

// The score ring. Deliberately NOT labelled with a percentage: passingScore is a
// COUNT of correct answers (see pre-quiz-step.tsx — the API computes Passed as
// correctAnswers.length >= quiz.passingScore), so every number the user reads
// here is "N of M correct". The ring is only a proportional visual; the design
// mock's "88%" would misstate the actual requirement.
let ScoreRing = ({
  score,
  total,
  passed,
}: {
  score: number
  total: number
  passed: boolean
}): ReactElement => {
  let theme = useTheme()
  let accent = passed ? theme.palette.success.main : theme.palette.warning.main

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress
        aria-hidden
        variant="determinate"
        size={160}
        thickness={3}
        value={100}
        sx={{ color: theme.palette.action.disabledBackground }}
      />
      <CircularProgress
        aria-hidden
        variant="determinate"
        size={160}
        thickness={3}
        // total can be 0 only for an empty quiz, which the pre-quiz gate
        // already refuses to start — guarded anyway so the ring never renders
        // NaN.
        value={total > 0 ? (score / total) * 100 : 0}
        sx={{ color: accent, position: 'absolute', left: 0 }}
      />
      <Box sx={{ position: 'absolute', textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontSize: '2.25rem', color: accent }}>
          {score}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          of {total}
        </Typography>
      </Box>
    </Box>
  )
}

// The scored-attempt surface for both outcomes. Which CTA it offers comes from
// the server's nextStep, never from re-reading quiz/survey/certificate state —
// one backend function produces nextStep and both the quiz result and the
// progress endpoint return that same value, so re-deriving it here is the one
// way the client could contradict the server about what a user owes.
//
// nextStep is null only if the result arrived without one, which the contract
// says can't happen for a scored attempt. Treated as "wait for the progress
// refetch" rather than as an error: the middleware refetches on scoring, and
// selectWizardPhase moves the user on regardless of what this screen renders.
export let QuizResultStep = ({
  result,
  onRetakeQuiz,
  onReviewContent,
  onContinue,
  isStartingRetake,
}: {
  result: CourseV2QuizResult
  onRetakeQuiz: () => void
  onReviewContent: () => void
  onContinue: () => void
  isStartingRetake: boolean
}): ReactElement => {
  let theme = useTheme()
  let { nextStep } = result
  let { passed, score, passingScore } = result.result
  // The answer count IS the question count for a scored attempt: scoring
  // requires every displayed question to have been answered, so this is the
  // denominator without needing the quiz record here.
  let total = result.answers.length

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
      <ScoreRing score={score} total={total} passed={passed} />

      <Typography variant="h1" sx={{ fontSize: '1.75rem', mt: 3, mb: 1 }}>
        {passed ? 'Assessment Passed' : 'Not quite there'}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {score} of {total} correct · you need {passingScore} of {total} to pass
      </Typography>

      <Divider sx={{ my: 4 }} />

      {passed ? (
        <>
          <Button
            variant="contained"
            color="success"
            size="large"
            endIcon={<RightArrowIcon />}
            onClick={onContinue}
            disabled={nextStep === null}
            sx={{ borderRadius: 8, px: 4 }}
          >
            {nextStep?.kind === 'submitSurvey'
              ? 'Continue to Survey'
              : 'Continue to Certificate'}
          </Button>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 0.75 }}
          >
            <ConfirmIcon sx={{ fontSize: 18, color: 'success.main' }} />
            {nextStep?.kind === 'submitSurvey'
              ? 'One final step to generate your CE certificate.'
              : 'Your CE certificate is being generated.'}
          </Typography>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={onRetakeQuiz}
              disabled={isStartingRetake}
              sx={{ borderRadius: 8, px: 4 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ReviewContentIcon />}
              onClick={onReviewContent}
              sx={{ borderRadius: 8, px: 4, color: theme.palette.text.primary }}
            >
              Review Course Content
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can retake this as many times as you need — there&apos;s no
            penalty for a second attempt.
          </Typography>
        </>
      )}
    </Box>
  )
}
